import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { JOB_CATEGORIES, EMPLOYMENT_TYPES, employmentLabel, formatSalary } from "@/lib/jobs";
import {
  Search, MapPin, Briefcase, Sparkles, Plus, Flame, BadgeCheck, Filter, Globe,
} from "lucide-react";

export default function Jobs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [remote, setRemote] = useState(false);
  const [workType, setWorkType] = useState("");   // "" | "in_person" | "remote" | "hybrid"
  const [emp, setEmp] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (category) params.set("category", category);
    if (location.trim()) params.set("location", location.trim());
    if (remote) params.set("remote_ok", "true");
    if (workType) params.set("work_type", workType);
    if (emp) params.set("employment_type", emp);
    try {
      const { data } = await api.get(`/jobs?${params.toString()}`);
      setJobs(data || []);
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { load(); /* initial */ }, []);

  const onFilterChange = () => { load(); };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q]);

  const boosted = useMemo(() => (jobs || []).filter(j => j.is_boosted), [jobs]);
  const organic = useMemo(() => (jobs || []).filter(j => !j.is_boosted), [jobs]);

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10" data-testid="jobs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1">Find Job · Hire People</p>
          <h1 className="h-display text-4xl sm:text-5xl">Work that fits your life.</h1>
          <p className="text-[#4A4A4A] mt-2 max-w-xl">
            Real humans posting real roles — full-time, part-time, projects. Apply directly on Hoomanz.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/jobs/applications" className="btn-ghost" data-testid="my-applications">
            My applications
          </Link>
          <Link to="/jobs/mine" className="btn-ghost" data-testid="my-job-posts">
            My posts
          </Link>
          <Link to="/jobs/new" className="btn-primary" data-testid="post-job-cta">
            <Plus size={14}/> Post a job
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mt-7" data-testid="jobs-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-5 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Search title, company, keywords…"
              value={q} onChange={(e)=>setQ(e.target.value)} data-testid="jobs-search"/>
          </div>
          <div className="md:col-span-3 relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Location" value={location}
              onChange={(e)=>setLocation(e.target.value)} onBlur={onFilterChange}
              data-testid="jobs-location"/>
          </div>
          <select className="input md:col-span-2" value={category}
            onChange={(e)=>{ setCategory(e.target.value); setTimeout(load, 0); }}
            data-testid="jobs-category">
            <option value="">All categories</option>
            {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="input md:col-span-2" value={emp}
            onChange={(e)=>{ setEmp(e.target.value); setTimeout(load, 0); }}
            data-testid="jobs-employment">
            <option value="">Any type</option>
            {EMPLOYMENT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="jobs-work-type-filter">
          {[
            { id: "",          label: "All" },
            { id: "in_person", label: "In person" },
            { id: "remote",    label: "Remote" },
            { id: "hybrid",    label: "Hybrid" },
          ].map(o => (
            <button key={o.id} type="button"
              onClick={()=>{ setWorkType(o.id); setTimeout(load, 0); }}
              data-testid={`jobs-work-type-${o.id || "all"}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                workType === o.id ? "bg-[#050505] text-white" : "bg-[#F5F5F5] text-[#454545] hover:bg-[#E8E8E8]"
              }`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="text-sm text-[#B84B4B] mt-4">{err}</p>}

      {/* Lists */}
      {jobs === null && <p className="text-[#8A8A8A] mt-10">Loading…</p>}

      {jobs && jobs.length === 0 && (
        <div className="text-center py-20" data-testid="jobs-empty">
          <Briefcase size={32} className="mx-auto text-[#0057FF] mb-3"/>
          <p className="text-[#4A4A4A]">No jobs match your filters yet.</p>
          {user && <Link to="/jobs/new" className="btn-secondary mt-4 inline-flex"><Plus size={14}/> Be the first to post</Link>}
        </div>
      )}

      {boosted.length > 0 && (
        <section className="mt-8">
          <p className="overline mb-3 text-[#0057FF] inline-flex items-center gap-1.5">
            <Flame size={12}/> Boosted
          </p>
          <div className="grid gap-3" data-testid="jobs-boosted">
            {boosted.map(j => <JobRow key={j.id} j={j} onClick={()=>nav(`/jobs/${j.id}`)} boosted/>)}
          </div>
        </section>
      )}

      {organic.length > 0 && (
        <section className="mt-8">
          {boosted.length > 0 && <p className="overline mb-3 inline-flex items-center gap-1.5"><Filter size={12}/> All jobs</p>}
          <div className="grid gap-3" data-testid="jobs-list">
            {organic.map(j => <JobRow key={j.id} j={j} onClick={()=>nav(`/jobs/${j.id}`)}/>)}
          </div>
        </section>
      )}
    </div>
  );
}

function JobRow({ j, onClick, boosted }) {
  return (
    <div onClick={onClick}
      data-testid={`job-row-${j.id}`}
      className={`card p-5 cursor-pointer hover:border-[#0A0A0A] transition-colors ${boosted ? "ring-1 ring-[#0057FF]/40" : ""}`}>
      <div className="flex items-start gap-3">
        <img src={j.employer?.avatar} alt="" className="h-12 w-12 rounded-full object-cover bg-[#E5E5E3]"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5">
            <h3 className="font-medium text-lg leading-tight">{j.title}</h3>
            {boosted && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white">
                {(j.boost_tier || "boosted").toString()}
              </span>
            )}
            {j.has_applied && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white">Applied</span>}
          </div>
          <p className="text-sm text-[#4A4A4A] mt-0.5">
            <span className="font-medium">{j.company_name || j.employer?.name}</span>
            {j.employer?.verified && <BadgeCheck size={12} className="inline ml-1 -mt-0.5 text-[#0A0A0A]"/>}
            <span className="mx-1.5 opacity-50">·</span>
            {j.location || (j.work_type === "remote" ? "Remote" : "—")}
            {(j.work_type === "remote" || j.remote_ok) && <span className="ml-1 text-[#8A8A8A]">· Remote</span>}
            {j.work_type === "hybrid" && <span className="ml-1 text-[#8A8A8A]">· Hybrid</span>}
          </p>
          <p className="text-sm text-[#0A0A0A] mt-2 line-clamp-2">{j.description}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-medium">{formatSalary(j)}</p>
          <p className="text-xs text-[#8A8A8A] mt-1">{employmentLabel(j.employment_type)}</p>
          <p className="text-xs text-[#8A8A8A] mt-1 inline-flex items-center gap-1"><Sparkles size={10}/> {j.application_count || 0} applied</p>
        </div>
      </div>
    </div>
  );
}
